import React, { useState } from 'react';
import { Message } from '../types';
import { t, Lang } from '../../../../i18n';

interface SubtaskSelectorMessageProps {
  message: Message;
  lang?: Lang;
  onSelectSubtasks: (message: Message, selectedSubtaskIds: string[]) => void;
}

const SubtaskSelectorMessage: React.FC<SubtaskSelectorMessageProps> = ({ 
  message, 
  lang = 'en',
  onSelectSubtasks 
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isExpanded, setIsExpanded] = useState(true);

  const subtasks = message.subtasks || [];

  const toggleSubtask = (subtaskId: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(subtaskId)) {
      newSelected.delete(subtaskId);
    } else {
      newSelected.add(subtaskId);
    }
    setSelectedIds(newSelected);
  };

  const handleSubmit = () => {
    if (selectedIds.size > 0) {
      onSelectSubtasks(message, Array.from(selectedIds));
    }
  };

  return (
    <div className="subtask-selector-message" style={{ marginTop: '8px' }}>
      <div 
        className="subtask-selector-header"
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          cursor: 'pointer',
          padding: '8px 12px',
          backgroundColor: '#f3eaff',
          borderRadius: '8px',
          border: '1px solid #c7a8ff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          userSelect: 'none'
        }}
      >
        <span style={{ fontWeight: '500', color: '#6c3483' }}>
          {t(lang, 'selectSubtasks')}
        </span>
        <span style={{ color: '#6c3483', fontSize: '12px' }}>
          {isExpanded ? '▼' : '▶'} {selectedIds.size > 0 && `(${selectedIds.size} ${t(lang, 'selected')})`}
        </span>
      </div>
      
      {isExpanded && (
        <div 
          className="subtask-selector-content"
          style={{
            marginTop: '8px',
            backgroundColor: '#fafafa',
            borderRadius: '8px',
            border: '1px solid #e0e0e0',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <div 
            style={{
              padding: '12px',
              maxHeight: '300px',
              overflowY: 'auto'
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {subtasks.map((subtask) => {
                const isSelected = selectedIds.has(subtask.id);
                return (
                  <label
                    key={subtask.id}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      padding: '8px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      backgroundColor: isSelected ? '#e6d6f7' : 'transparent',
                      border: `1px solid ${isSelected ? '#c7a8ff' : '#e0e0e0'}`,
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSubtask(subtask.id)}
                      style={{
                        marginRight: '8px',
                        marginTop: '2px',
                        cursor: 'pointer'
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: isSelected ? '500' : '400', color: '#333' }}>
                        {subtask.id}: {subtask.name}
                      </div>
                      {subtask.description && (
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                          {subtask.description}
                        </div>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
          
          <div style={{ padding: '12px', paddingTop: '0' }}>
            <button
              onClick={handleSubmit}
              disabled={selectedIds.size === 0}
              style={{
                marginTop: '12px',
                padding: '8px 16px',
                backgroundColor: selectedIds.size > 0 ? '#6c3483' : '#ccc',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: selectedIds.size > 0 ? 'pointer' : 'not-allowed',
                fontSize: '14px',
                fontWeight: '500',
                width: '100%',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => {
                if (selectedIds.size > 0) {
                  e.currentTarget.style.backgroundColor = '#5a2970';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedIds.size > 0) {
                  e.currentTarget.style.backgroundColor = '#6c3483';
                }
              }}
            >
              {t(lang, 'submit')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubtaskSelectorMessage;

